import { useEffect, useState, useRef, useMemo } from "react";
import { Circle, Image, Layer, Stage } from "react-konva";
import useImage from "use-image";
import { ClockArm } from "./ClockArm";

import hoursArmImg from "./assets/hoursarm.png";
import minutesArmImg from "./assets/minutesarm.png";
import secondsArmImg from "./assets/secondsarm.png";

import twelveImg from "./assets/12.png";
import threeImg from "./assets/3.png";
import sixImg from "./assets/6.png";
import nineImg from "./assets/9.png";
import leafImg from "./assets/leaf.png";

import gem1gImg from "./assets/gem1g.png";
import gem2gImg from "./assets/gem2g.png";
import gem1pImg from "./assets/gem1p.png";
import gem2pImg from "./assets/gem2p.png";
import { WSClient } from "./ws-client";
import { Message } from "./message";
import { useLocalStorage } from "./useLocalStorage";
import { BreakLayer } from "./BreakGlass";

interface ClockProps {
  ws: WSClient;
}

const transitionDuration: number = 2000;

function wrapSetTimeWithLocalStorage(setTime: (newTime: number | ((prev: number) => number)) => void) {
  return (newTime: number | ((prev: number) => number)) => {
    if (typeof newTime === "number") {
      window.localStorage.setItem("impprod.clock.clock-time", newTime.toString());
    } else {
      window.localStorage.setItem("impprod.clock.clock-time", newTime(parseInt(window.localStorage.getItem("impprod.clock.clock-time") ?? "10000" as string, 10)).toString());
    }
    setTime(newTime);
  }
}

export function Clock({ ws }: ClockProps) {
  const [time, setTime] = useState<number>(parseInt(window.localStorage.getItem("impprod.clock.clock-time") ?? "10000" as string, 10));
  const [am, setAm] = useState<boolean>(true);
  const [run, setRun] = useState<boolean>(true);
  const [_rotationEnabled, setRotationEnabled] = useState<boolean>(false);
  const [breakAllowed, setBreakAllowed] = useState<boolean>(false);
  const broken = useRef<boolean>(false);
  const [hoursAngle, setHoursAngle] = useState<number>(0);
  const [minutesAngle, setMinutesAngle] = useState<number>(0);
  const [resetBreak, setResetBreak] = useState<boolean>(false);
  const [timeBeforeBreak, setTimeBeforeBreak] = useState<number>(0);

  const [twelve] = useImage(twelveImg);
  const [three] = useImage(threeImg);
  const [six] = useImage(sixImg);
  const [nine] = useImage(nineImg);
  const [leaf] = useImage(leafImg);
  const [gem1g] = useImage(gem1gImg);
  const [gem2g] = useImage(gem2gImg);
  const [gem1p] = useImage(gem1pImg);
  const [gem2p] = useImage(gem2pImg);

  const width: number = window.innerWidth;
  const height: number = window.innerHeight;
  const center: [number, number] = [width / 2, height / 2];

  const hoursActive = hoursAngle > 179 && hoursAngle < 181;
  const minutesActive = minutesAngle > 179 && minutesAngle < 181;

  const rotationEnabled = _rotationEnabled && !(hoursActive && minutesActive);

  const animationRef = useRef<number | null>(null);

  const setTimePersistent = wrapSetTimeWithLocalStorage(setTime);

  function animateTime(current: number, target: number, duration: number) {
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }

    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const interpolated = current + (target - current) * progress;
      setTimePersistent(interpolated);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(step);
  }

  useEffect(() => {
    const off = ws.onMessage((msg: Message) => {
      if (msg.type === "set-time") {
        console.log("Set time:", msg.timestamp);
        const [hours, minutes, seconds]: number[] = msg.timestamp.split(":").map(Number);
        const target = hours * 3600 + minutes * 60 + seconds;
        animateTime(time, target, transitionDuration);
      } else if (msg.type === "sync") {
        console.log("Sync time:", msg.timestamp);
        const [hours, minutes, seconds]: number[] = msg.timestamp.split(":").map(Number);
        const target = hours * 3600 + minutes * 60 + seconds;
        setTimePersistent(target);
      } else if (msg.type === "allow-break") {
        console.log("Break allowed");
        setBreakAllowed(true);
      } else if (msg.type === "reset") {
        console.log("Reset");
        setTimePersistent((prev) => prev + 1);
        setBreakAllowed(false);
        broken.current = false;
        setRotationEnabled(false);
        setRun(true);
        setHoursAngle(0);
        setMinutesAngle(0);
        setResetBreak((prev) => !prev);
      } else {
        console.error("Unknown message type:", msg.type);
      }
    });

    return off;
  }, [ws, time]);

  useEffect(() => {
    const h = Math.floor((time / 3600) % 24);
    if (h % 12 === 0) {
      setAm((prev) => !prev);
    }
  }, [time]);

  useEffect(() => {
    if (run) {
      const interval = setInterval(() => {
        setTimePersistent((t) => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [run]);

  const hour = Math.floor((time / 3600) % 12);
  const minute = Math.floor((time / 60) % 60);
  const second = Math.floor(time % 60);

  return (
    <div className="clock">
      <Stage width={width} height={height}>
        <Layer listening={false}>
          <Circle
            visible={
              (rotationEnabled || broken.current) && minutesActive
            }
            name="minutes-circle"
            x={center[0]}
            y={center[1]}
            radius={190}
            fill="transparent"
            stroke="white"
            strokeWidth={5}
            shadowBlur={15}
            shadowOffset={{ x: 0, y: 0 }}
            shadowOpacity={1}
            shadowColor="#FF3333"
            opacity={0.5}
          />
          <Circle
            visible={
              (rotationEnabled || broken.current) && hoursActive
            }
            name="hours-circle"
            x={center[0]}
            y={center[1]}
            radius={150}
            fill="transparent"
            stroke="white"
            strokeWidth={5}
            shadowBlur={15}
            shadowOffset={{ x: 0, y: 0 }}
            shadowOpacity={1}
            shadowColor="#00ffff"
            opacity={0.5}
          />
        </Layer>
        <Layer>
          <Image image={twelve} x={center[0]} y={35} offsetX={55} offsetY={30} width={110} height={60} shadowBlur={3} shadowOffset={{ x: 0, y: 0 }} shadowOpacity={0.8} />
          <Image image={three} x={width - 45} y={center[1]} offsetX={50} offsetY={30} width={100} height={60} shadowBlur={3} shadowOffset={{ x: 0, y: 0 }} shadowOpacity={0.8} />
          <Image image={six} x={center[0]} y={height - 40} offsetX={45} offsetY={40} width={90} height={80} shadowBlur={3} shadowOffset={{ x: 0, y: 0 }} shadowOpacity={0.8} />
          <Image image={nine} x={45} y={center[1]} offsetX={55} offsetY={38} width={110} height={76} shadowBlur={3} shadowOffset={{ x: 0, y: 0 }} shadowOpacity={0.8} />
        </Layer>
        <Layer>
          <ClockArm
            img={hoursArmImg}
            length={240}
            width={50}
            scale={0.8}
            startRotation={getRotationFromTime(time, "hours")}
            position={center}
            offset={[-25, -195]}
            // onManualRotateStart={() => setRun(false)}
            // onManualRotateEnd={() => setRun(true)}
            // onRotate={(angle) => setTimePersistent((prev) => (Math.floor((angle / 360) * 12) * 3600) + (prev % 3600))}
            onRotate={(angle) => setHoursAngle(angle)}
            manualRotationEnabled={rotationEnabled}
          />
          <ClockArm
            img={minutesArmImg}
            length={270}
            width={50}
            scale={0.9}
            startRotation={getRotationFromTime(time, "minutes")}
            position={center}
            offset={[-25, -230]}
            // onManualRotateStart={() => setRun(false)}
            // onManualRotateEnd={() => setRun(true)}
            // onRotate={(angle) => setTimePersistent((prev) => (Math.floor((angle / 360) * 60) * 60) + (prev % 60))}
            onRotate={(angle) => setMinutesAngle(angle)}
            manualRotationEnabled={rotationEnabled}
          />
          <ClockArm
            img={secondsArmImg}
            length={270}
            width={30}
            scale={0.9}
            startRotation={getRotationFromTime(time, "seconds")}
            position={center}
            offset={[-13, -225]}
            // onManualRotateStart={() => setRun(false)}
            // onManualRotateEnd={() => setRun(true)}
            // onRotate={(angle) => setTimePersistent((prev) => Math.floor((angle / 360) * 60) + (prev - (prev % 60)))}
            manualRotationEnabled={rotationEnabled}
          />
        </Layer>

        <Layer>
          <Image image={leaf} x={center[0]} y={center[1]} offsetX={35} offsetY={50} width={70} height={100} scale={{ x: 0.7, y: 0.7 }} rotation={54} shadowBlur={3} shadowOffset={{ x: 1, y: 1 }} shadowOpacity={0.8} />
          <Image image={gem1g} x={width - 120} y={height - 120} scale={{ x: 0.5, y: 0.5 }} shadowBlur={3} shadowOffset={{ x: 1, y: 1 }} shadowOpacity={0.8} />
          <Image image={gem2g} x={-40} y={-60} rotation={0} scale={{ x: 0.5, y: 0.5 }} shadowBlur={3} shadowOffset={{ x: 1, y: 1 }} shadowOpacity={0.8} />
          <Image image={gem1p} x={-100} y={height - 30} rotation={-45} scale={{ x: 0.5, y: 0.5 }} shadowBlur={3} shadowOffset={{ x: 1, y: 1 }} shadowOpacity={0.8} />
          <Image image={gem2p} x={width - 160} y={50} rotation={-50} scale={{ x: 0.5, y: 0.5 }} shadowBlur={3} shadowOffset={{ x: 1, y: 1 }} shadowOpacity={0.8} />
        </Layer>
        <BreakLayer
          key={resetBreak ? "reset" : "break"}
          allow={breakAllowed}
          point={{ x: center[0], y: center[1] }}
          onBreak={() => {
            setTimeBeforeBreak(time);
            setRun(false);
            setRotationEnabled(true);
            broken.current = true;
          }}
        />
      </Stage>
    </div>
  );
}

function getRotationFromTime(time: number, unit: "hours" | "minutes" | "seconds"): number {
  switch (unit) {
    case "hours":
      return ((time / 3600) % 12) * 30;
    case "minutes":
      return ((time / 60) % 60) * 6;
    case "seconds":
      return (time % 60) * 6;
  }
}
